import { useState } from 'react'
import { SimpleForm, TextInput, SaveButton, Toolbar } from 'react-admin'
import { Typography, Grid } from '@mui/material'
import { FieldValues } from 'react-hook-form'
import { PartnerGeneralInfo } from '../types/PartnerGeneralInfo'
import { AddressVerification } from './AddressVerification'
import { AddressData, getAddresses } from '../repository/getAddresses'
import useAlert from '@/components/Alert/hooks/useAlert'

export type AddressInfoFormProps = {
  partner: PartnerGeneralInfo
}

const styles = {
  typography: {
    marginTop: 2
  }
}

const validateAddress = (data: AddressData) => {
  const errors = []
  if (!data.address_1) errors.push('Address is required')
  if (!data.city) errors.push('City is required')
  if (!data.state) errors.push('State is required')
  if (!data.zip) errors.push('Zip code is required')
  if (!data.country) errors.push('Country is required')
  return errors
}

const SaveOnlyToolbar = () => (
  <Toolbar>
    <SaveButton />
  </Toolbar>
)

export const AddressInfoForm = ({ partner }: AddressInfoFormProps) => {
  const { alertErrors } = useAlert()
  const [modalVisible, setModalVisible] = useState(false)
  const toggleModal = () => setModalVisible((visible) => !visible)

  const [providedAddress, setProvidedAddress] = useState<AddressData | null>(
    null
  )
  const [suggestedAddress, setSuggestedAddress] = useState<AddressData | null>(
    null
  )
  const [addressStatus, setAddressStatus] = useState<'invalid' | 'suggested'>()
  const [addressError, setAddressError] = useState<string[]>([])

  const handleAddress = async (values: FieldValues) => {
    const formAddress = {
      address_1: values.address1,
      address_2: values.address2,
      city: values.city,
      country: values.country,
      phone_number: values.fulfillmentPhoneNumber,
      state: values.state,
      zip: values.zipcode
    }

    const errors = validateAddress(formAddress)
    if (errors.length > 0) {
      alertErrors(errors)
      return
    }

    setProvidedAddress(formAddress)

    const response = await getAddresses(formAddress)

    if (response.kind === 'error') {
      alertErrors([response.message])
    } else {
      const suggestedAddress = response.data.attributes.suggested_address
      const addressStatus = response.data.attributes.status
      const addressErrors = response.data.attributes.errors.map(
        (error: { field: string; message: string }) => error.message
      )

      setSuggestedAddress(suggestedAddress)
      setAddressStatus(addressStatus)
      setAddressError(addressErrors)
      toggleModal()
    }
  }

  return (
    <SimpleForm
      data-testid="update-address-form"
      toolbar={<SaveOnlyToolbar />}
      onSubmit={handleAddress}
      record={partner}
    >
      <Typography variant="h6" gutterBottom sx={styles.typography}>
        Address Information
      </Typography>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 4 }}>
          <TextInput
            source="address1"
            label="Return Address"
            fullWidth
            inputProps={{ 'data-testid': 'address-1' }}
          />
        </Grid>

        <Grid size={{ xs: 12, md: 4 }}>
          <TextInput
            source="address2"
            label="Return Address Line 2"
            fullWidth
            inputProps={{ 'data-testid': 'address-2' }}
          />
        </Grid>

        <Grid size={{ xs: 12, md: 4 }}>
          <TextInput
            source="city"
            label="City"
            fullWidth
            inputProps={{ 'data-testid': 'city' }}
          />
        </Grid>

        <Grid size={{ xs: 12, md: 4 }}>
          <TextInput
            source="state"
            label="State"
            fullWidth
            inputProps={{ 'data-testid': 'state' }}
          />
        </Grid>

        <Grid size={{ xs: 12, md: 4 }}>
          <TextInput
            source="zipcode"
            label="Zipcode"
            fullWidth
            inputProps={{ 'data-testid': 'zip-code' }}
          />
        </Grid>

        <Grid size={{ xs: 12, md: 4 }}>
          <TextInput
            source="country"
            label="Country"
            fullWidth
            inputProps={{ 'data-testid': 'country' }}
          />
        </Grid>
      </Grid>
      <AddressVerification
        open={modalVisible}
        onClose={toggleModal}
        providedAddress={providedAddress}
        verifiedAddress={suggestedAddress}
        addressStatus={addressStatus}
        addressError={addressError}
      />
    </SimpleForm>
  )
}
